/**
 * PhotoRealCommunityIcons
 *
 * Skeuomorphic 64×64 SVG illustrations for the Community/People module.
 * All icons reference shared gradients from SharedSvgDefs (pr-* IDs).
 * Mount SharedSvgDefs once at app root before using these.
 *
 * Module accent: blue #2563a8
 */

import { forwardRef, type SVGProps } from "react";

type IconProps = SVGProps<SVGSVGElement>;

// ═══════════════════════════════════════════════════════════════
// NAVIGATION — FloatingNav group icons + items
// ═══════════════════════════════════════════════════════════════

/** Two people — People group icon */
export const PRPeopleGroup = forwardRef<SVGSVGElement, IconProps>(
  (props, ref) => (
    <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <ellipse cx="32" cy="56" rx="22" ry="4" fill="#000" opacity={0.06} />
      {/* Person 1 (back) */}
      <circle cx="24" cy="20" r="8" fill="url(#pr-comm-blue)" opacity={0.6} />
      <ellipse cx="24" cy="40" rx="12" ry="10" fill="url(#pr-comm-blue)" opacity={0.4} />
      {/* Person 2 (front) */}
      <circle cx="38" cy="22" r="9" fill="url(#pr-comm-blue)" />
      <circle cx="38" cy="22" r="9" fill="url(#pr-glass-shine)" opacity={0.3} />
      <ellipse cx="38" cy="44" rx="13" ry="10" fill="url(#pr-comm-blue-deep)" />
      {/* Connecting line */}
      <path d="M28 36c4-2 8-2 12 0" stroke="#2563a8" strokeWidth="1" opacity={0.3} strokeDasharray="2 2" />
    </svg>
  ),
);
PRPeopleGroup.displayName = "PRPeopleGroup";

/** Directory / address book */
export const PRPeopleDirectory = forwardRef<SVGSVGElement, IconProps>(
  (props, ref) => (
    <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <ellipse cx="32" cy="56" rx="22" ry="4" fill="#000" opacity={0.06} />
      {/* Book body */}
      <rect x="12" y="8" width="40" height="44" rx="3" fill="url(#pr-comm-blue)" />
      <rect x="12" y="8" width="40" height="44" rx="3" fill="url(#pr-glass-shine)" opacity={0.2} />
      {/* Pages */}
      <rect x="16" y="10" width="34" height="40" rx="2" fill="url(#pr-paper-white)" />
      {/* Tab markers */}
      <rect x="50" y="14" width="4" height="6" rx="1" fill="url(#pr-comm-blue)" opacity={0.8} />
      <rect x="50" y="24" width="4" height="6" rx="1" fill="url(#pr-comm-blue)" opacity={0.6} />
      <rect x="50" y="34" width="4" height="6" rx="1" fill="url(#pr-comm-blue)" opacity={0.4} />
      {/* Person icon */}
      <circle cx="33" cy="24" r="5" fill="url(#pr-comm-blue-light)" />
      <circle cx="33" cy="24" r="5" stroke="url(#pr-comm-blue)" strokeWidth="1" opacity={0.5} />
      <circle cx="33" cy="22" r="2" fill="url(#pr-comm-blue)" opacity={0.5} />
      <ellipse cx="33" cy="27" rx="3" ry="2" fill="url(#pr-comm-blue)" opacity={0.3} />
      {/* Name lines */}
      <rect x="22" y="33" width="22" height="2" rx="1" fill="url(#pr-comm-blue-light)" />
      <rect x="22" y="38" width="16" height="2" rx="1" fill="url(#pr-comm-blue-light)" />
      <rect x="22" y="43" width="18" height="2" rx="1" fill="url(#pr-comm-blue-light)" />
    </svg>
  ),
);
PRPeopleDirectory.displayName = "PRPeopleDirectory";

/** Two arrows merging — Merge duplicates */
export const PRPeopleMerge = forwardRef<SVGSVGElement, IconProps>(
  (props, ref) => (
    <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <ellipse cx="32" cy="56" rx="20" ry="4" fill="#000" opacity={0.06} />
      {/* Two person cards */}
      <rect x="6" y="12" width="22" height="28" rx="3" fill="url(#pr-paper-white)" stroke="url(#pr-comm-blue)" strokeWidth="1" opacity={0.7} />
      <circle cx="17" cy="22" r="4" fill="url(#pr-comm-blue)" opacity={0.4} />
      <rect x="10" y="30" width="14" height="2" rx="1" fill="url(#pr-comm-blue-light)" />
      <rect x="36" y="12" width="22" height="28" rx="3" fill="url(#pr-paper-white)" stroke="url(#pr-comm-blue)" strokeWidth="1" opacity={0.7} />
      <circle cx="47" cy="22" r="4" fill="url(#pr-comm-blue)" opacity={0.4} />
      <rect x="40" y="30" width="14" height="2" rx="1" fill="url(#pr-comm-blue-light)" />
      {/* Merge arrows */}
      <path d="M22 34l10 10" stroke="url(#pr-comm-blue-deep)" strokeWidth="2" strokeLinecap="round" />
      <path d="M42 34l-10 10" stroke="url(#pr-comm-blue-deep)" strokeWidth="2" strokeLinecap="round" />
      {/* Merged result */}
      <circle cx="32" cy="46" r="5" fill="url(#pr-comm-blue)" />
      <path d="M29.5 46l1.5 1.5 3-3" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
);
PRPeopleMerge.displayName = "PRPeopleMerge";

/** Contact card with blue accent — Member directory */
export const PRPeopleContact = forwardRef<SVGSVGElement, IconProps>(
  (props, ref) => (
    <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <ellipse cx="32" cy="56" rx="22" ry="4" fill="#000" opacity={0.06} />
      {/* Card */}
      <rect x="6" y="12" width="52" height="36" rx="4" fill="url(#pr-paper-white)" />
      <rect x="6" y="12" width="52" height="36" rx="4" stroke="#2563a8" strokeWidth="1" opacity={0.3} />
      {/* Blue sidebar */}
      <rect x="6" y="12" width="6" height="36" rx="3" fill="url(#pr-comm-blue)" />
      {/* Avatar */}
      <circle cx="24" cy="26" r="7" fill="url(#pr-comm-blue-light)" />
      <circle cx="24" cy="24" r="3" fill="url(#pr-comm-blue)" opacity={0.5} />
      <ellipse cx="24" cy="30" rx="4.5" ry="3" fill="url(#pr-comm-blue)" opacity={0.3} />
      {/* Info lines */}
      <rect x="36" y="20" width="18" height="2.5" rx="1" fill="url(#pr-comm-blue)" opacity={0.6} />
      <rect x="36" y="26" width="14" height="2" rx="1" fill="url(#pr-comm-blue-light)" />
      <rect x="36" y="32" width="16" height="2" rx="1" fill="url(#pr-comm-blue-light)" />
      <rect x="36" y="38" width="12" height="2" rx="1" fill="url(#pr-comm-blue-light)" />
    </svg>
  ),
);
PRPeopleContact.displayName = "PRPeopleContact";

/** Heart with handshake — Engagement group icon */
export const PRPeopleEngagement = forwardRef<SVGSVGElement, IconProps>(
  (props, ref) => (
    <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <ellipse cx="32" cy="56" rx="20" ry="4" fill="#000" opacity={0.06} />
      {/* Heart */}
      <path
        d="M32 50 C16 38 4 26 14 16 c6-6 14-2 18 6 4-8 12-12 18-6 10 10-2 22-18 34z"
        fill="url(#pr-comm-blue)"
      />
      <path
        d="M32 50 C16 38 4 26 14 16 c6-6 14-2 18 6 4-8 12-12 18-6 10 10-2 22-18 34z"
        fill="url(#pr-glass-shine)" opacity={0.3}
      />
      {/* Handshake symbol */}
      <path d="M22 30l4-2h4l4 2" stroke="#fff" strokeWidth="2" strokeLinecap="round" opacity={0.8} />
      <path d="M34 30l4-2h4" stroke="#fff" strokeWidth="2" strokeLinecap="round" opacity={0.8} />
      <circle cx="30" cy="32" r="1.5" fill="#fff" opacity={0.6} />
    </svg>
  ),
);
PRPeopleEngagement.displayName = "PRPeopleEngagement";

/** Engagement scoring — heart with bar chart */
export const PRPeopleEngagementScore = forwardRef<SVGSVGElement, IconProps>(
  (props, ref) => (
    <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <ellipse cx="32" cy="56" rx="20" ry="4" fill="#000" opacity={0.06} />
      {/* Card background */}
      <rect x="8" y="8" width="48" height="44" rx="4" fill="url(#pr-paper-white)" />
      <rect x="8" y="8" width="48" height="44" rx="4" stroke="#2563a8" strokeWidth="1" opacity={0.3} />
      {/* Bar chart */}
      <rect x="14" y="32" width="8" height="14" rx="2" fill="url(#pr-comm-blue-light)" />
      <rect x="26" y="26" width="8" height="20" rx="2" fill="url(#pr-comm-blue)" opacity={0.6} />
      <rect x="38" y="20" width="8" height="26" rx="2" fill="url(#pr-comm-blue-deep)" />
      {/* Heart icon on top */}
      <path d="M32 18 C28 14 22 14 22 18 c0 4 10 8 10 8 s10-4 10-8 c0-4-6-4-10 0z" fill="url(#pr-coral)" opacity={0.8} />
      {/* Trend arrow */}
      <path d="M16 38l10-8 8 4 12-10" stroke="url(#pr-comm-blue)" strokeWidth="1.5" strokeLinecap="round" fill="none" />
      <path d="M44 22l4 2-2 4" stroke="url(#pr-comm-blue)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  ),
);
PRPeopleEngagementScore.displayName = "PRPeopleEngagementScore";

/** Person with plus badge — Visitor follow-up */
export const PRPeopleVisitor = forwardRef<SVGSVGElement, IconProps>(
  (props, ref) => (
    <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <ellipse cx="32" cy="56" rx="20" ry="4" fill="#000" opacity={0.06} />
      {/* Person */}
      <circle cx="28" cy="22" r="9" fill="url(#pr-comm-blue)" />
      <circle cx="28" cy="22" r="9" fill="url(#pr-glass-shine)" opacity={0.3} />
      <ellipse cx="28" cy="44" rx="14" ry="10" fill="url(#pr-comm-blue-deep)" />
      {/* Plus badge */}
      <circle cx="48" cy="18" r="8" fill="url(#pr-felt-green)" />
      <path d="M48 14v8M44 18h8" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
      {/* Welcome wave lines */}
      <path d="M48 32c2 2 2 6 0 8" stroke="url(#pr-comm-blue)" strokeWidth="1.5" strokeLinecap="round" fill="none" opacity={0.4} />
      <path d="M52 30c3 3 3 10 0 12" stroke="url(#pr-comm-blue)" strokeWidth="1.5" strokeLinecap="round" fill="none" opacity={0.3} />
    </svg>
  ),
);
PRPeopleVisitor.displayName = "PRPeopleVisitor";

/** Cake with candle — Life events */
export const PRPeopleLifeEvents = forwardRef<SVGSVGElement, IconProps>(
  (props, ref) => (
    <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <ellipse cx="32" cy="56" rx="22" ry="4" fill="#000" opacity={0.06} />
      {/* Cake base */}
      <rect x="10" y="32" width="44" height="18" rx="4" fill="url(#pr-comm-blue-light)" />
      <rect x="10" y="32" width="44" height="18" rx="4" stroke="url(#pr-comm-blue)" strokeWidth="1" opacity={0.4} />
      {/* Frosting wave */}
      <path d="M10 36c4-4 8 0 12-4s8 0 12-4 8 0 12-4 4 0 8-4" stroke="url(#pr-comm-blue)" strokeWidth="2" opacity={0.3} fill="none" />
      {/* Middle layer */}
      <rect x="14" y="24" width="36" height="10" rx="3" fill="url(#pr-comm-blue)" opacity={0.3} />
      {/* Candles */}
      <rect x="22" y="14" width="3" height="12" rx="1" fill="url(#pr-glass-amber)" />
      <rect x="31" y="14" width="3" height="12" rx="1" fill="url(#pr-comm-blue)" opacity={0.6} />
      <rect x="40" y="14" width="3" height="12" rx="1" fill="url(#pr-coral)" opacity={0.6} />
      {/* Flames */}
      <ellipse cx="23.5" cy="12" rx="2" ry="3" fill="url(#pr-glass-amber)" opacity={0.8} />
      <ellipse cx="23.5" cy="12" rx="1" ry="2" fill="#fde68a" />
      <ellipse cx="32.5" cy="12" rx="2" ry="3" fill="url(#pr-glass-amber)" opacity={0.8} />
      <ellipse cx="32.5" cy="12" rx="1" ry="2" fill="#fde68a" />
      <ellipse cx="41.5" cy="12" rx="2" ry="3" fill="url(#pr-glass-amber)" opacity={0.8} />
      <ellipse cx="41.5" cy="12" rx="1" ry="2" fill="#fde68a" />
    </svg>
  ),
);
PRPeopleLifeEvents.displayName = "PRPeopleLifeEvents";

/** Winding road with milestones — Membership pathways */
export const PRPeoplePathways = forwardRef<SVGSVGElement, IconProps>(
  (props, ref) => (
    <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <ellipse cx="32" cy="56" rx="22" ry="4" fill="#000" opacity={0.06} />
      {/* Winding path */}
      <path
        d="M12 50 C20 44 20 36 32 32 C44 28 44 20 52 14"
        stroke="url(#pr-comm-blue)" strokeWidth="4" strokeLinecap="round" fill="none" opacity={0.3}
      />
      <path
        d="M12 50 C20 44 20 36 32 32 C44 28 44 20 52 14"
        stroke="url(#pr-comm-blue)" strokeWidth="2" strokeLinecap="round" fill="none" strokeDasharray="4 4"
      />
      {/* Milestone dots */}
      <circle cx="14" cy="48" r="4" fill="url(#pr-comm-blue)" opacity={0.4} />
      <circle cx="32" cy="32" r="5" fill="url(#pr-comm-blue)" opacity={0.7} />
      <circle cx="50" cy="16" r="6" fill="url(#pr-comm-blue-deep)" />
      {/* Star at destination */}
      <path d="M50 12l1.5 3 3 .5-2 2 .5 3-3-1.5-3 1.5.5-3-2-2 3-.5z" fill="url(#pr-gold-face)" />
      {/* Person at start */}
      <circle cx="14" cy="48" r="2.5" fill="url(#pr-comm-blue)" />
    </svg>
  ),
);
PRPeoplePathways.displayName = "PRPeoplePathways";
